// Roles, tabs, and capabilities — mirror of services/api-gateway/app/permissions.py.
// Keep in sync. The backend is authoritative — this file drives UI nav only.
// See .claude/plans/roles-permissions-overhaul.md for the full spec.

export const Role = Object.freeze({
  HERDSMAN: 'herdsman',
  MANAGER: 'manager',
  BUSH_CLEARER: 'bush_clearer',
  BIOCHAR_OPERATOR: 'biochar_operator',
  LAND_MONITOR: 'land_monitor',
  OWNER: 'owner',
  VIEWER: 'viewer',
  SYSTEM_ADMIN: 'system_admin',
  EXTERNAL_AUDITOR: 'external_auditor',  // mig 093: time-bounded read-only auditor (CAL etc.)
  ASSURANCE_OFFICER: 'assurance_officer',  // mig 121: WildeBeef staff — cross-farm read + adjudication, no data entry
})

// Legacy alias — "admin" is still accepted server-side as a system_admin.
const SYSTEM_ADMIN_ALIASES = new Set([Role.SYSTEM_ADMIN, 'admin'])

export const Tab = Object.freeze({
  DASHBOARD: 'dashboard',
  CATTLE: 'cattle',
  // Offline-first kraal census capture (Session 4).
  CENSUS: 'census',
  DAIRY: 'dairy',
  PADDOCKS: 'paddocks',
  LANTANA: 'lantana',
  GRAZING: 'grazing',
  FINANCE: 'finance',
  // Drug/vaccine stock (migration 110): purchases, counts, mass-balance.
  INVENTORY: 'inventory',
  MARKET_PRICES: 'market_prices',
  LAND_MONITORING: 'land_monitoring',
  BIOCHAR: 'biochar',
  REPORTS: 'reports',
  AGENTS: 'agents',
  // In-app owner assistant (Q&A chat + weekly digest). Web surface for the
  // WhatsApp agent. Finance answers gated server-side by ranch role.
  ASSISTANT: 'assistant',
  USERS: 'users',
  MY_LANTANA_ASSIGNMENTS: 'my_lantana_assignments',
  MY_BIOCHAR_BATCHES: 'my_biochar_batches',
  // Biochar pilot land monitoring (mig 092 + 093)
  PILOT_PROJECTS: 'pilot_projects',
  AUDITOR_PORTAL: 'auditor_portal',
  // AI model registry (mig 098): sysadmin-only nav.
  AI_MODELS: 'ai_models',
  // Fleet Assurance (mig 109): sysadmin-only cross-farm trust-score worklist.
  FLEET_ASSURANCE: 'fleet_assurance',
  // Digital twin (mig 130): base/actual/best trajectories + assumptions
  // register. Frontend-only tab like CENSUS — backend routes gate on ranch
  // membership (assert_ranch_access); assumption WRITES are owner-gated
  // server-side.
  TWIN: 'twin',
  // Twin Fleet (plan Phase 6): cross-farm twin worklist. Nav for sysadmin +
  // assurance officer (like FLEET_ASSURANCE); the backend itself is
  // membership-based, so a future multi-farm owner can reach /twin/fleet
  // directly even without the nav entry.
  TWIN_FLEET: 'twin_fleet',
  // Pasture pins (mig 124, plan pasture-pin-protocol.md Phase 2): the
  // herdsman capture surface. Frontend-only tab like CENSUS — the backend
  // gates writes on Capability.WRITE_GRAZING, not a Tab.
  PASTURE: 'pasture',
})

const MANAGER_TABS = new Set([
  Tab.DASHBOARD,
  Tab.CATTLE,
  Tab.CENSUS,
  Tab.DAIRY,
  Tab.PADDOCKS,
  Tab.LANTANA,
  Tab.GRAZING,
  Tab.PASTURE,
  Tab.FINANCE,
  Tab.INVENTORY,
  Tab.MARKET_PRICES,
  Tab.BIOCHAR,
  Tab.REPORTS,
  Tab.TWIN,
  Tab.ASSISTANT,
  Tab.USERS,
])

// S6 (owner read-only experience): owners get the focused read surface —
// Assistant, Weekly Report, Dashboard, Cattle, Paddocks, Finance, Grazing,
// plus a read-only Inventory stock view. Operational tabs (census capture,
// dairy, lantana, market prices, biochar, pilots) are the manager's
// workspace. Paddocks restored for owners 2026-07-04 (Ernest): the land is
// the asset — owners view paddocks read-only (writes stay MANAGE_PADDOCKS).
// Kept in sync with _OWNER_TABS in app/permissions.py (backend has no
// CENSUS/INVENTORY tab — frontend-only surfaces).
const OWNER_TABS = new Set([
  Tab.DASHBOARD,
  Tab.CATTLE,
  Tab.PADDOCKS,
  Tab.GRAZING,
  Tab.FINANCE,
  Tab.INVENTORY,
  Tab.REPORTS,
  Tab.TWIN,
  Tab.ASSISTANT,
])
// Viewer mirrors the owner's read surface.
const VIEWER_TABS = new Set([...OWNER_TABS])

// Roles whose web experience is read-only: no entry buttons, no forms.
// Owners keep exactly two governance verbs (finance approval + grazing-week
// unlock), which render regardless of this flag. The assurance officer is
// read-only by constitution (they certify records, so they never create
// them); their adjudication verbs (plan item B) will render regardless of
// this flag, like the owner's governance verbs.
export const READ_ONLY_ROLES = new Set([
  Role.OWNER, Role.VIEWER, Role.EXTERNAL_AUDITOR, Role.ASSURANCE_OFFICER,
])

export const ROLE_TABS = Object.freeze({
  [Role.HERDSMAN]: new Set([Tab.CATTLE, Tab.CENSUS, Tab.DAIRY, Tab.GRAZING, Tab.PASTURE]),
  [Role.MANAGER]: MANAGER_TABS,
  [Role.OWNER]: OWNER_TABS,
  [Role.VIEWER]: VIEWER_TABS,
  [Role.BUSH_CLEARER]: new Set([Tab.MY_LANTANA_ASSIGNMENTS]),
  [Role.BIOCHAR_OPERATOR]: new Set([Tab.MY_BIOCHAR_BATCHES]),
  [Role.LAND_MONITOR]: new Set([Tab.LAND_MONITORING]),
  // External auditor (mig 093): scoped read-only on the auditor portal only.
  [Role.EXTERNAL_AUDITOR]: new Set([Tab.AUDITOR_PORTAL]),
  // Assurance officer (mig 121): the owner's read surface (they investigate
  // what owners are told) + Inventory (drug mass-balance flags) + the
  // cross-farm Fleet Assurance worklist — their cockpit.
  [Role.ASSURANCE_OFFICER]: new Set([...OWNER_TABS, Tab.FLEET_ASSURANCE, Tab.TWIN_FLEET]),
  [Role.SYSTEM_ADMIN]: new Set(Object.values(Tab)),
})

export function canonicalRole(roleStr) {
  if (!roleStr) return null
  if (SYSTEM_ADMIN_ALIASES.has(roleStr)) return Role.SYSTEM_ADMIN
  return Object.values(Role).includes(roleStr) ? roleStr : null
}

export function isSystemAdmin(roleStr) {
  return SYSTEM_ADMIN_ALIASES.has(roleStr)
}

export function tabsForRole(roleStr) {
  const role = canonicalRole(roleStr)
  if (!role) return new Set()
  return ROLE_TABS[role] ?? new Set()
}

export function canSeeTab(roleStr, tab) {
  return tabsForRole(roleStr).has(tab)
}

// SmartKraal registration review (D15 Phase 3): the assurance officer and the
// system admin confirm / send back tag bindings. Owners hold the adjudication
// capability on the exceptions ledger but do not review bindings (decision
// 2026-10-31); routes/smartkraal_registration.py enforces the same.
export const REGISTRATION_REVIEWER_ROLES = new Set([Role.ASSURANCE_OFFICER, Role.SYSTEM_ADMIN])

export function canReviewRegistration(roleStr) {
  const role = canonicalRole(roleStr)
  return !!role && REGISTRATION_REVIEWER_ROLES.has(role)
}

// Session 4: system-owner tier vocabulary + break-glass header names. The
// backend drives all gating; these constants exist so UI code can name them
// without magic strings when attaching headers to destructive admin calls.
export const Tier = Object.freeze({ T0: '0', T1: '1', T2: '2' })
export const BREAKGLASS_HEADER = 'X-Breakglass-Reason'
export const APPROVAL_REQUEST_HEADER = 'X-Approval-Request-Id'

// Helper: pull pending_request out of a 403 error body from a T2 call.
// The backend returns a detail object like:
//   { message, pending_request: { id, required_count, domain, action_manifest } }
// Returns null when the error is not a tier-2 quorum response.
export function extractPendingTierRequest(errDetail) {
  if (!errDetail || typeof errDetail !== 'object') return null
  if (errDetail.pending_request && errDetail.pending_request.id) {
    return errDetail.pending_request
  }
  return null
}

export const ROLE_LABELS = Object.freeze({
  [Role.HERDSMAN]: 'Herdsman',
  [Role.MANAGER]: 'Manager',
  [Role.BUSH_CLEARER]: 'Bush Clearer',
  [Role.BIOCHAR_OPERATOR]: 'Biochar Operator',
  [Role.LAND_MONITOR]: 'Land Monitor',
  [Role.OWNER]: 'Owner',
  [Role.VIEWER]: 'Viewer',
  [Role.EXTERNAL_AUDITOR]: 'External Auditor',
  [Role.ASSURANCE_OFFICER]: 'Assurance Officer',
  [Role.SYSTEM_ADMIN]: 'System Owner',
  admin: 'System Owner',
})
